import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BookTemplate, Plus, Edit2, Trash2, History, RotateCcw, Users, Share2, Upload, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import VariablesHelp from "@/components/VariablesHelp";

const SAMPLE_BODY = `Ante el Juzgado de {{tribunal}}

D./Dña. {{nombre_cliente}}, con NIF {{nif_cliente}}, representado por el procurador {{procurador}} y asistido por el letrado {{letrado}}, comparece ante este Juzgado y como mejor proceda en Derecho DICE:

HECHOS
PRIMERO.- {{hecho_primero}}

FUNDAMENTOS DE DERECHO
PRIMERO.- {{fundamento_primero}}

Por lo expuesto,
SUPLICO AL JUZGADO que, tenga por presentado este escrito, lo admita, y acuerde {{suplico}}.

En {{ciudad}}, a {{fecha}}.`;

const MATERIAS = ["Civil", "Penal", "Laboral", "Mercantil", "Administrativo", "Familia", "Fiscal", "Otro"];

export default function Templates() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const empty = { name: "", category: "", materia: "Civil", tags: [], description: "", body: SAMPLE_BODY, is_shared: false };
  const [form, setForm] = useState(empty);
  const [tagInput, setTagInput] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/templates");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setTagInput(""); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, category: t.category || "", materia: t.materia || "Civil",
      tags: t.tags || [], description: t.description || "", body: t.body,
      is_shared: !!t.is_shared,
    });
    setTagInput("");
    setOpen(true);
  };

  const addTag = (v) => {
    const t = (v || tagInput).trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const rmTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/templates/${editing.id}`, form);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/templates", form);
        toast.success("Plantilla creada");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar plantilla?")) return;
    try { await api.delete(`/templates/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const showVersions = async (t) => {
    setVersionsFor(t);
    try {
      const { data } = await api.get(`/templates/${t.id}/versions`);
      setVersions(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const rollback = async (version) => {
    if (!confirm(`¿Restaurar versión v${version}? Se archivará la actual.`)) return;
    try {
      await api.post(`/templates/${versionsFor.id}/rollback/${version}`);
      toast.success("Versión restaurada");
      setVersionsFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const canEdit = user?.role === "admin" || user?.role === "abogado";
  const docxInputRef = useRef(null);
  const [uploadingDocx, setUploadingDocx] = useState(false);

  const onDocxSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".docx")) {
      toast.error("Solo se admiten archivos .docx");
      e.target.value = "";
      return;
    }
    setUploadingDocx(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("materia", "Civil");
      const { data } = await api.post("/templates/upload-docx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Plantilla creada: ${data.name}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingDocx(false);
      if (docxInputRef.current) docxInputRef.current.value = "";
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Plantillas"
        subtitle="Plantillas jurídicas reutilizables, con versionado y sugerencias inteligentes. Actívalas como compartidas para que aparezcan en el Marketplace del despacho."
        actions={canEdit && (
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".docx"
              ref={docxInputRef}
              onChange={onDocxSelected}
              className="hidden"
              data-testid="template-docx-input"
            />
            <Button
              variant="outline"
              onClick={() => docxInputRef.current?.click()}
              disabled={uploadingDocx}
              data-testid="upload-docx-button"
              className="rounded-sm border-[#E5E7EB]"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingDocx ? "Importando…" : "Importar DOCX"}
            </Button>
            <Button onClick={openNew} data-testid="new-template-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
              <Plus className="w-4 h-4 mr-2" />Nueva plantilla
            </Button>
          </div>
        )}
      />

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <BookTemplate className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">Aún no tienes plantillas. Crea la primera.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-[#E5E7EB] p-5 hover:shadow-sm transition-shadow" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-widest text-neutral-500">{t.materia || "Sin materia"}</span>
                    <span className="text-neutral-300">·</span>
                    <span className="font-mono text-[10px] text-neutral-500">v{t.version || 1}</span>
                    {t.is_shared && (
                      <Badge className="rounded-sm text-[10px] uppercase bg-[#7B1826]/10 text-[#7B1826] hover:bg-[#7B1826]/10 border-[#7B1826]/30 border">
                        <Share2 className="w-2.5 h-2.5 mr-1" /> Compartida
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-display text-xl mt-0.5 text-[#0F172A] truncate">{t.name}</h3>
                  {t.category && <div className="text-xs text-neutral-500 mt-0.5">{t.category}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button onClick={() => showVersions(t)} className="p-1.5 text-neutral-500 hover:text-[#7B1826]" title="Historial" data-testid={`template-history-${t.id}`}><History className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(t)} className="p-1.5 text-neutral-500 hover:text-[#7B1826]" title="Editar"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(t.id)} className="p-1.5 text-neutral-500 hover:text-[#DC2626]" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              {t.description && <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{t.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-4">
                {(t.variables || []).slice(0, 6).map((v) => (
                  <Badge key={v} variant="outline" className="rounded-sm font-mono text-[10px] border-[#E5E7EB]">
                    {"{{"+v+"}}"}
                  </Badge>
                ))}
                {(t.variables || []).length > 6 && <span className="text-[10px] text-neutral-400">+{t.variables.length - 6}</span>}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F3F4F6] text-[11px] text-neutral-500">
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{t.usage_count || 0} usos</span>
                {t.created_by_name && <span className="truncate">{t.created_by_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" className="rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Demanda, Recurso..." className="rounded-sm mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Materia</Label>
                <Select value={form.materia} onValueChange={(v) => setForm({ ...form, materia: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="template-materia-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MATERIAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end justify-between border border-[#E5E7EB] rounded-sm px-4 h-[74px] mt-[22px]">
                <div className="flex-1">
                  <div className="text-sm font-medium">Compartir en Marketplace</div>
                  <div className="text-[11px] text-neutral-500">Visible para el despacho</div>
                </div>
                <Switch
                  checked={form.is_shared}
                  onCheckedChange={(v) => setForm({ ...form, is_shared: v })}
                  data-testid="template-shared-switch"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Etiquetas</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-1">
                {form.tags.map((t) => (
                  <Badge key={t} variant="outline" className="rounded-sm border-[#E5E7EB] gap-1 pr-1">
                    {t}
                    <button onClick={() => rmTag(t)} className="hover:text-[#DC2626] ml-1">×</button>
                  </Badge>
                ))}
              </div>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Escribe una etiqueta y pulsa Enter"
                className="rounded-sm"
                data-testid="template-tags-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Cuerpo de la plantilla — usa <code className="font-mono text-[#7B1826]">{"{{variable}}"}</code>
              </Label>
              <div className="mt-1">
                <RichTextEditor
                  value={form.body}
                  onChange={(html) => setForm({ ...form, body: html })}
                  placeholder="Redacta aquí la plantilla. Las variables {{expediente.numero}}, {{cliente.nombre}}, etc. se rellenan automáticamente."
                />
              </div>
              <VariablesHelp />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.body} data-testid="template-save-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
              {editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsFor} onOpenChange={(v) => !v && setVersionsFor(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Historial de versiones</DialogTitle>
            <div className="text-sm text-neutral-500">{versionsFor?.name}</div>
          </DialogHeader>
          {versions.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Esta plantilla aún no tiene versiones archivadas.</div>
          ) : (
            <div className="space-y-3">
              {versions.map((v) => (
                <div key={v.version} className="border border-[#E5E7EB] p-3 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-mono text-xs text-neutral-500">v{v.version}</div>
                      <div className="text-xs text-neutral-400 font-mono">{new Date(v.saved_at).toLocaleString()}</div>
                    </div>
                    <Button onClick={() => rollback(v.version)} size="sm" variant="outline" className="rounded-sm" data-testid={`rollback-v${v.version}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                    </Button>
                  </div>
                  <div className="text-xs text-neutral-600 line-clamp-3 font-mono whitespace-pre-wrap">{v.body}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
